/**
 * Тексты страниц витрины: «Политика конфиденциальности» и «Пользовательское
 * соглашение» (R-115). У страницы три языка, у языка — заголовок и текст в
 * HTML. Сервер принимает только те языки, которые прислали, и у каждого
 * присланного требует и заголовок, и текст.
 *
 * Здесь — человеческая сторона: названия, черновик правок и проверка до
 * отправки, чтобы отказ сервера не был первым, что увидит редактор.
 */
import { useState } from 'react'

export type PageSlug = 'privacy' | 'terms'
export type PageLang = 'ru' | 'uz' | 'en'

export const PAGE_NAME: Record<PageSlug, string> = {
  privacy: 'Политика конфиденциальности',
  terms: 'Пользовательское соглашение',
}

export const LANG_NAME: Record<PageLang, string> = {
  ru: 'Русский',
  uz: 'Oʻzbekcha',
  en: 'English',
}

export const PAGE_SLUGS: PageSlug[] = ['privacy', 'terms']
export const PAGE_LANGS: PageLang[] = ['ru', 'uz', 'en']

/**
 * Границы сервера (service/content.go). Заголовок — в буквах; текст — длина
 * строки HTML, которую хранит сервер, а не видимых букв: разметка тоже в счёт.
 */
export const MAX_TITLE = 200
export const MAX_BODY = 100_000

export interface PageText {
  title: string
  body: string
}

/** Правка одного языка одной страницы и то, с чего она началась. */
export interface PageEdit extends PageText {
  slug: PageSlug
  lang: PageLang
  base: PageText
}

/** Правки по `slug:lang`. Пусто — правок нет. */
export type PageDraft = Record<string, PageEdit>

export const editKey = (slug: PageSlug, lang: PageLang) => `${slug}:${lang}`

/** Язык не заполнен: пустые и заголовок, и текст. */
export const isEmpty = (text: PageText) => !text.title.trim() && !text.body.trim()

/** Правки, которые действительно что-то меняют, — только их и отправляем. */
export function changedEdits(draft: PageDraft): PageEdit[] {
  return Object.values(draft).filter(
    (edit) => edit.title !== edit.base.title || edit.body !== edit.base.body,
  )
}

const letters = (text: string) => [...text.trim()].length

export interface PageProblem {
  title?: string
  body?: string
}

/**
 * Что не так с языком перед отправкой. Сервер требует оба поля у каждого
 * присланного языка, поэтому заполненный текст без заголовка — ошибка, и
 * наоборот.
 */
export function problemOf(text: PageText): PageProblem | null {
  const problem: PageProblem = {}
  if (!text.title.trim()) problem.title = 'Нужен заголовок'
  else if (letters(text.title) > MAX_TITLE) problem.title = `Не длиннее ${MAX_TITLE} знаков`
  if (!text.body.trim()) problem.body = 'Нужен текст'
  else if (text.body.length > MAX_BODY) problem.body = 'Не длиннее 100 000 знаков'
  return problem.title || problem.body ? problem : null
}

/** Тело запросов: по одному на страницу, в каждом — только изменённые языки. */
export function toRequests(
  edits: PageEdit[],
): { slug: PageSlug; texts: Partial<Record<PageLang, PageText>> }[] {
  const bySlug = new Map<PageSlug, Partial<Record<PageLang, PageText>>>()
  edits.forEach((edit) => {
    const texts = bySlug.get(edit.slug) ?? {}
    texts[edit.lang] = { title: edit.title, body: edit.body }
    bySlug.set(edit.slug, texts)
  })
  return [...bySlug].map(([slug, texts]) => ({ slug, texts }))
}

/**
 * Состояние вкладки «Тексты страниц». Живёт в экране настроек, а не во
 * вкладке: правки не пропадают, если перейти на соседнюю вкладку и вернуться.
 */
export function usePageTexts() {
  const [slug, setSlug] = useState<PageSlug>('privacy')
  const [lang, setLang] = useState<PageLang>('ru')
  const [draft, setDraft] = useState<PageDraft>({})
  // Растёт при сбросе правок: редактор текста пересоздаётся с сохранённым.
  const [version, setVersion] = useState(0)
  return {
    slug,
    setSlug,
    lang,
    setLang,
    draft,
    setDraft,
    version,
    discard: () => {
      setDraft({})
      setVersion((v) => v + 1)
    },
  }
}

export type PageTextsState = ReturnType<typeof usePageTexts>
